import React from 'react';
import {
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    OverlayDrawer,
    Button,
    Text,
    Accordion,
    AccordionItem,
    AccordionHeader,
    AccordionPanel,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { useCandidateDetailsPanelStyles } from './CandidateDetailsPanel.styles';
import { StatusBadge } from '../StatusBadge';
import { RollupStatus } from '../../types';

export interface OnboardingStep {
    id: string;
    name: string;
    status: RollupStatus;
    /** The detailed status label from the API (e.g. "Document accepted", "To be defined") */
    statusLabel?: string;
    date?: string;
}

export interface OnboardingSection {
    id: string;
    name: string;
    steps: OnboardingStep[];
}

export interface CandidateDetails {
    id: number;
    name: string;
    jobTitle: string;
    startDate: string;
    location: string;
    currentStage: string;
    email: string;
    caseNumber: string;
    requisitionNumber: string;
    hrCaseNumber: string;
    hiringManagerEmail?: string;
    onboardingSections: OnboardingSection[];
}

interface CandidateDetailsPanelProps {
    isOpen: boolean;
    onClose: () => void;
    candidate: CandidateDetails | null;
}

export const CandidateDetailsPanel: React.FC<CandidateDetailsPanelProps> = ({
    isOpen,
    onClose,
    candidate,
}) => {
    const styles = useCandidateDetailsPanelStyles();

    if (!candidate) return null;

    const getStatusLabel = (status: RollupStatus): string => {
        switch (status) {
            case RollupStatus.Completed:
                return 'Completed';
            case RollupStatus.InProgress:
                return 'In progress';
            case RollupStatus.NotStarted:
                return 'Not Started';
            case RollupStatus.Expired:
                return 'Expired';
            default:
                return 'Not Started';
        }
    };



    return (
        <OverlayDrawer
            open={isOpen}
            onOpenChange={(_, { open }) => !open && onClose()}
            position="end"
            size="medium"
            style={{ top: '48px', height: 'calc(100vh - 48px)', width: '460px' }}
            backdrop={{ style: { top: '48px' } }}
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Close panel"
                            icon={<Dismiss24Regular />}
                            onClick={onClose}
                        />
                    }
                >
                    Candidate Details
                </DrawerHeaderTitle>
            </DrawerHeader>

            <DrawerBody className={styles.body}>
                {/* Basic Info Section */}
                <dl className={styles.infoSection}>
                    <div className={styles.infoRow}>
                        <dt className={styles.label}>Name</dt>
                        <dd className={styles.value}>{candidate.name}</dd>
                    </div>

                    <div className={styles.infoRow}>
                        <dt className={styles.label}>Job title</dt>
                        <dd className={styles.value}>{candidate.jobTitle}</dd>
                    </div>

                    <div className={styles.infoRow}>
                        <dt className={styles.label}>Start date</dt>
                        <dd className={styles.value}>{candidate.startDate}</dd>
                    </div>

                    <div className={styles.infoRow}>
                        <dt className={styles.label}>Requisition number</dt>
                        <dd className={styles.value}>{candidate.requisitionNumber}</dd>
                    </div>

                    <div className={styles.infoRow}>
                        <dt className={styles.label}>Reference number</dt>
                        <dd className={styles.value}>{candidate.hrCaseNumber}</dd>
                    </div>

                    <div className={styles.infoRow}>
                        <dt className={styles.label}>Location</dt>
                        <dd className={styles.value}>{candidate.location}</dd>
                    </div>

                    <div className={styles.infoRow}>
                        <dt className={styles.label}>Current stage</dt>
                        <dd className={styles.value}>{candidate.currentStage}</dd>
                    </div>

                    <div className={styles.infoRow}>
                        <dt className={styles.label}>Candidate email</dt>
                        <dd className={styles.emailContainer}>
                            <Text className={styles.value}>{candidate.email}</Text>
                        </dd>
                    </div>

                    {candidate.hiringManagerEmail && (
                        <div className={styles.infoRow}>
                            <dt className={styles.label}>Hiring manager email</dt>
                            <dd className={styles.emailContainer}>
                                <Text className={styles.value}>{candidate.hiringManagerEmail}</Text>
                            </dd>
                        </div>
                    )}
                </dl>

                {/* Onboarding Steps Section */}
                <div className={styles.stepsSection}>
                    <h3 className={styles.stepsTitle}>Onboarding steps</h3>
                    
                    {candidate.onboardingSections.map((section) => (
                        <Accordion
                            key={section.id}
                            collapsible
                            defaultOpenItems={[section.id]}
                        >
                            <AccordionItem value={section.id}>
                                <AccordionHeader className={styles.accordionHeader}>
                                    {section.name}
                                </AccordionHeader>
                                <AccordionPanel className={styles.accordionPanel}>
                                    {section.steps.map((step) => (
                                        <div key={step.id} className={styles.stepRow}>
                                            <Text className={styles.stepName}>{step.name}</Text>
                                            {step.date ? (
                                                <div className={styles.dateRow}>
                                                    <Text className={styles.stepDate}>{step.date}</Text>
                                                    <StatusBadge status={step.status} label={step.statusLabel || getStatusLabel(step.status)} />
                                                </div>
                                            ) : (
                                                <StatusBadge status={step.status} label={step.statusLabel || getStatusLabel(step.status)} />
                                            )}
                                        </div>
                                    ))}
                                </AccordionPanel>
                            </AccordionItem>
                        </Accordion>
                    ))}
                </div>

                {/* Need Help Section */}
                <div className={styles.needHelpSection}>
                    <h3 className={styles.needHelpTitle}>Need help?</h3>
                    <Text className={styles.needHelpText}>
                        Email AskHR at{' '}
                        <a
                            href={`mailto:askhr@microsoft.com?subject=Pre-Hire%20Activity%20Enquiry%20%E2%80%93%20Case%20Number%20${encodeURIComponent(candidate.caseNumber)}&body=Hello%2C%0A%0AI%20am%20reaching%20out%20regarding%20my%20pre-hire%20activities.%0A%0AMy%20reference%20case%20number%20is%20${encodeURIComponent(candidate.caseNumber)}%0A%0AI%20would%20appreciate%20your%20assistance%20with%20%5Bbriefly%20describe%20the%20issue%20you%20are%20facing%5D.%0A%0AThank%20you`}
                            className={styles.needHelpLink}
                        >
                            askhr@microsoft.com
                        </a>
                    </Text>
                    <Text className={styles.needHelpHint}>
                        For a faster response, include the candidate's HR case number in the subject line,{' '}
                        <strong>{candidate.caseNumber}</strong>.
                    </Text>
                </div>

                {/* Close Button */}
                <div className={styles.footer}>
                    <Button appearance="secondary" onClick={onClose}>
                        Close
                    </Button>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
};
